import { useState, useEffect } from 'react';
import { useNavigate, useParams, generatePath } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store';
import { PermissionEnum, DeviceSessionSidepanel } from '@ui/src';
import {
  useEndCustomerSessionMutation,
  useLazyGetCustomerDeviceSessionsQuery,
} from '../store/services/individual';
import { Device } from '../types';
import { clearDeviceState } from '../store/slices/devices';

import ROUTES from '../../../routes';

const IndividualCustomerDeviceItem = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    individualCustomers: {
      info: {
        devices: { devices, deviceSessions, deviceSessionsPagination },
      },
    },
  } = useAppSelector((state) => state);
  const { activePermissions } = useAppSelector((state) => state.root);

  const { deviceId, customerId } = useParams();

  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [device, setDevice] = useState<Device | undefined>(undefined);

  const [endSession, { isLoading, isSuccess }] =
    useEndCustomerSessionMutation();

  const [
    getDeviceSessions,
    { isLoading: deviceSessionsIsLoading, isFetching },
  ] = useLazyGetCustomerDeviceSessionsQuery();

  const handleClose = () => {
    setPanelIsOpen(false);
    setDevice(undefined);
    dispatch(clearDeviceState());

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_DEVICES, {
          customerId: customerId as string,
        }),
      );
    }, 100);
  };

  const onLinkClick = () => {
    dispatch(clearDeviceState());
  };

  useEffect(() => {
    if (deviceId && devices) {
      const currentDevice = devices?.find((item) => item.id === deviceId);

      if (currentDevice) {
        setDevice(currentDevice);
      }
    }
  }, [deviceId, devices]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  return (
    <DeviceSessionSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      deleteSessionIsAvailable={activePermissions?.includes(
        PermissionEnum.CUSTOMERS_SESSIONS_DELETE_ACTION,
      )}
      isLoading={isLoading}
      onEndSession={() => {
        endSession({
          id: customerId as string,
          deviceId: deviceId as string,
        });
      }}
      device={device}
      itemId={customerId}
      deviceId={deviceId}
      onLinkClick={onLinkClick}
      deviceSessions={deviceSessions}
      activePermissions={activePermissions}
      isFetching={isFetching}
      deviceSessionsIsLoading={deviceSessionsIsLoading}
      deviceSessionsPagination={deviceSessionsPagination}
      auditLink={ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOGS_ITEM}
      getDeviceSessions={getDeviceSessions}
      sessionIsAvailable={activePermissions?.includes(
        PermissionEnum.CUSTOMERS_DEVICE_SESSIONS_LIST,
      )}
      backLink={generatePath(
        ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_DEVICES_ITEM,
        { customerId: customerId as string, deviceId: deviceId as string },
      )}
      customerDevices
    />
  );
};

export default IndividualCustomerDeviceItem;
